/**
 * Endoscopy still-image viewer — contact-sheet grid + focused viewport
 * with clinical labelling.
 *
 * Two tiers of labelling (see lesionTaxonomy.ts + the research doc):
 *
 *   1. IMAGE LABEL — what the WHOLE frame shows. One anatomical
 *      landmark plus one lesion (or an explicit "Normal — no lesion"),
 *      each chosen from the procedure's controlled vocabulary
 *      (colonoscopy / upper-GI) or entered as free text, with the
 *      lesion's recognised grading scheme (Paris/NICE/Forrest/…) when
 *      it has one. Editable + clearable.
 *
 *   2. ANNOTATIONS — marked regions: freehand, arrow, distance,
 *      polygon (segmentation), bounding box, text. Each carries a
 *      lesion class from the same vocabulary (preset or custom).
 *      Editable (re-class) + deletable.
 *
 * Measurement: endoscopy DICOMs carry NO pixel-spacing tag (Modality
 * ES — confirmed against the object store), so millimetres cannot be
 * derived from the pixel grid. The reader calibrates against a
 * known-size in-frame reference (e.g. open biopsy forceps ≈ 7 mm) with
 * the Calibrate tool; the distance tool then reports mm. Until then it
 * reports px and the toolbar flags the frame uncalibrated.
 *
 * The frame JPEG is served stripped of its DICOM wrapper at
 * `/viewer-api/image/.../{uid}.jpg` so the browser renders via <img>
 * with no client-side codec. Labels persist server-side per
 * (instance, user) — see imageAnnoApi.ts.
 */
import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { createStore, produce } from "solid-js/store";
import type { StudyInstance } from "@viewer/api/client";
import { auth } from "@viewer/auth/store";
import {
  type ImageDoc, type ImageLabel, type Calibration,
  emptyDoc, loadImageDoc, saveImageDoc,
} from "./imageAnnoApi";
import { type Procedure, type Taxon, taxaFor, taxonById } from "./lesionTaxonomy";

export interface StillsViewProps {
  studyUid:    string;
  /** Every instance in the active stills series. */
  instances:   StudyInstance[];
  /** SOP Instance UID currently focused; the parent shell controls
   *  this signal so the explorer tree on the left can drive it. */
  activeInstanceUid?: string;
  /** Notify the parent when the user clicks a thumbnail in the
   *  contact sheet. Parent updates `activeInstance` accordingly. */
  onPickInstance?: (uid: string) => void;
  /** Save the report draft (same action as the right-dock "Save draft"
   *  button). Surfaced in the stills toolbar so it is reachable in
   *  fullscreen, where the right dock is hidden. */
  onSaveDraft?: () => void | Promise<void>;
  /** Live save state from the parent ("idle" | "saving" | "saved" | "error"). */
  saveState?: () => "idle" | "saving" | "saved" | "error";
}

/** Tool kinds.
 *   pan / zoom — viewport gestures, no persisted geometry
 *   freehand   — captured polyline of points (px in image space)
 *   arrow      — single segment, head at the second point
 *   distance   — single segment, labelled in mm (calibrated) or px
 *   polygon    — closed multi-vertex region (segmentation)
 *   bbox       — axis-aligned bounding box (two corners)
 *   text       — anchored caption box
 *   calibrate  — draw over a known-size reference to set mm-per-pixel
 */
type Tool = "select" | "pan" | "zoom" | "freehand" | "arrow" | "distance"
          | "polygon" | "bbox" | "text" | "calibrate";

interface Pt { x: number; y: number }

interface AnnoBase {
  id:    string;
  tool:  Tool;
  /** Stroke/fill — taken from the lesion class so the mark is
   *  colour-coded by what it marks. */
  color: string;
  /** Lesion class this mark belongs to (lesionTaxonomy.ts id), so the
   *  annotation carries clinical meaning into the ML-export path, not
   *  just a colour. Empty id + a name means a custom free-text class. */
  lesion_id?:   string;
  lesion_name?: string;
  /** Grade within the class's scheme (Paris/NICE/Forrest/…), if set. */
  grade_id?:    string;
}
interface AnnoFreehand extends AnnoBase { tool: "freehand"; pts: Pt[] }
interface AnnoArrow    extends AnnoBase { tool: "arrow";    a: Pt; b: Pt }
interface AnnoDistance extends AnnoBase { tool: "distance"; a: Pt; b: Pt }
interface AnnoPolygon  extends AnnoBase { tool: "polygon";  pts: Pt[] }
interface AnnoRect     extends AnnoBase { tool: "bbox";     a: Pt; b: Pt }
interface AnnoText     extends AnnoBase { tool: "text";     at: Pt; text: string }

type Annotation = AnnoFreehand | AnnoArrow | AnnoDistance | AnnoPolygon | AnnoRect | AnnoText;

const STROKE = "#27a567";   // fallback when a mark has no class colour
const STROKE_HALO = "#0f172a";

/** Short, stable case identifier from the study UID — always present,
 *  so an anonymised study (no name/MRN) is still identifiable. */
function caseId(studyUid: string): string {
  const digits = (studyUid || "").replace(/\D/g, "");
  const tail = (digits || studyUid || "").slice(-8);
  return tail ? `C-${tail}` : "C-unknown";
}

/** Tools whose draft is a single drag → two points [a, b]. */
const TWO_PT: ReadonlySet<Tool> = new Set(["arrow", "distance", "bbox", "calibrate"]);
/** Tools that produce a persisted annotation (vs a pure gesture). */
function isAnnotationTool(t: Tool): boolean {
  return t === "freehand" || t === "arrow" || t === "distance"
      || t === "polygon"  || t === "bbox"  || t === "text";
}

export function StillsView(p: StillsViewProps) {
  // Default to Select so a reader can click an existing mark to edit
  // it the moment a labelled image opens. Drawing tools are one click
  // away in the toolbar.
  const [tool, setTool] = createSignal<Tool>("select");
  const [zoom, setZoom] = createSignal(1);
  const [pan,  setPan]  = createSignal<Pt>({ x: 0, y: 0 });

  // The currently-selected annotation (Select tool). Drives the
  // on-image handles + the editable property panel on the right.
  const [selectedId, setSelectedId] = createSignal<string | null>(null);

  // Stroke/handle/font sizes are kept CONSTANT IN SCREEN PIXELS by
  // dividing the screen size by the current zoom — geometry lives in
  // image-pixel space (the SVG viewBox is the natural image), so a raw
  // stroke-width of 3 renders ~1px when a 1816px-wide frame is fit to
  // the panel. That illegible hairline + tiny font was the "blends
  // into the background / too small to read" problem. With this, a
  // line is always ~2.5 screen-px and a value label always ~16 screen-px.
  const sz = createMemo(() => {
    const z = Math.max(zoom(), 0.0001);
    return {
      main:   2.6 / z,   // primary stroke
      halo:   6.5 / z,   // dark casing behind the stroke
      hit:    18  / z,   // invisible fat hit-target for select/drag
      font:   17  / z,   // measurement / value labels
      txt:    20  / z,   // text-caption annotations
      handle: 8   / z,   // draggable control-point radius
      sel:    2   / z,   // selection outline
    };
  });

  // Per-instance docs (image label + calibration + annotations). We
  // hold the full map so switching back to a labelled still is instant;
  // saves are fired per mutation via mutateDoc.
  const [docs, setDocs] = createStore<Record<string, ImageDoc>>({});

  // Procedure (upper-GI / colonoscopy) selects the controlled
  // vocabulary. It is conceptually per-study, so we keep a session
  // signal that is seeded from the first labelled image and written
  // into every doc. Defaults to upper-GI (EGD).
  const [procedure, setProcedureSig] = createSignal<Procedure>("egd");

  // Active lesion class for the NEXT annotation (replaces the old
  // generic ClassBar). Either a taxonomy id, or a custom free-text name.
  const [annoLesionId,     setAnnoLesionId]     = createSignal<string>("");
  const [annoLesionCustom, setAnnoLesionCustom] = createSignal<string>("");
  const [annoGradeId,      setAnnoGradeId]      = createSignal<string>("");

  // Draft state for in-progress marks.
  const [draftPts,   setDraftPts]   = createSignal<Pt[] | null>(null);
  const [draftStart, setDraftStart] = createSignal<Pt | null>(null);
  const [polyHover,  setPolyHover]  = createSignal<Pt | null>(null);  // polygon rubber-band

  const [imgEl, setImgEl] = createSignal<HTMLImageElement | null>(null);
  // Natural image dims, REACTIVE — the SVG overlay is sized/viewBox'd
  // from these. Reading img.naturalWidth in JSX is non-reactive (0 at
  // first paint while loading), which sized the overlay 0×0 and clipped
  // every mark to nothing. Capturing into a signal on load is what makes
  // marks actually appear.
  const [natW, setNatW] = createSignal(0);
  const [natH, setNatH] = createSignal(0);
  function captureDims(el: HTMLImageElement | null | undefined) {
    if (el && el.naturalWidth) { setNatW(el.naturalWidth); setNatH(el.naturalHeight); }
  }

  const dpr = () => window.devicePixelRatio || 1;
  const [fitMode, setFitMode] = createSignal(true);

  let viewportEl!: HTMLDivElement;
  let rootEl!:     HTMLDivElement;

  // ---------- doc accessors ----------
  const curUid = () => p.activeInstanceUid;
  const curDoc = createMemo<ImageDoc>(() => {
    const uid = p.activeInstanceUid;
    return (uid && docs[uid]) || emptyDoc();
  });
  function mutateDoc(uid: string, fn: (d: ImageDoc) => void) {
    setDocs(produce((m) => {
      if (!m[uid]) m[uid] = emptyDoc();
      m[uid].procedure = procedure();   // keep the doc's procedure current
      fn(m[uid]);
    }));
    void saveImageDoc(p.studyUid, uid, docs[uid]);
  }
  const getAnnos = (uid: string | undefined): Annotation[] =>
    ((uid && (docs[uid]?.annotations as Annotation[])) || []);

  function pushAnno(a: Annotation) {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.annotations = [...d.annotations, a]; });
  }
  function removeAnno(id: string) {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.annotations = d.annotations.filter((x: any) => x.id !== id); });
  }
  function reclassAnno(id: string, lesionId: string) {
    const uid = curUid(); if (!uid) return;
    const tx = taxonById(lesionId);
    mutateDoc(uid, (d) => {
      d.annotations = d.annotations.map((a: any) =>
        a.id === id
          ? { ...a, lesion_id: tx?.id ?? "", lesion_name: tx?.name ?? "", color: tx?.color ?? STROKE, grade_id: "" }
          : a);
    });
  }
  function setAnnoGrade(id: string, gradeId: string) {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.annotations = d.annotations.map((a: any) => a.id === id ? { ...a, grade_id: gradeId } : a); });
  }
  /** Set a free-text custom lesion label on an annotation (any tool):
   *  clears the preset id/grade, keeps the mark's colour. */
  function setAnnoCustomLesion(id: string, name: string) {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => {
      d.annotations = d.annotations.map((a: any) =>
        a.id === id ? { ...a, lesion_id: "", lesion_name: name, grade_id: "" } : a);
    });
  }
  function setAnnoText(id: string, text: string) {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.annotations = d.annotations.map((a: any) => a.id === id ? { ...a, text } : a); });
  }
  const selectedAnno = createMemo<Annotation | undefined>(() =>
    getAnnos(curUid()).find((a) => a.id === selectedId()));

  // ---------- direct geometry editing (Select tool) ----------
  // Control points exposed as draggable handles. arrow/distance/bbox
  // expose their two defining points; polygon every vertex; freehand +
  // text move as a whole (no per-point handles).
  function handlesOf(a: Annotation): { idx: number; x: number; y: number }[] {
    if (a.tool === "arrow" || a.tool === "distance" || a.tool === "bbox") return [{ idx: 0, ...a.a }, { idx: 1, ...a.b }];
    if (a.tool === "polygon") return a.pts.map((q, i) => ({ idx: i, x: q.x, y: q.y }));
    return [];
  }
  function moveAnno(a: any, dx: number, dy: number) {
    if (a.pts) a.pts = a.pts.map((q: Pt) => ({ x: q.x + dx, y: q.y + dy }));
    if (a.a)   a.a  = { x: a.a.x + dx, y: a.a.y + dy };
    if (a.b)   a.b  = { x: a.b.x + dx, y: a.b.y + dy };
    if (a.at)  a.at = { x: a.at.x + dx, y: a.at.y + dy };
  }
  function movePoint(a: any, idx: number, dx: number, dy: number) {
    if (a.tool === "polygon") { const q = a.pts[idx]; a.pts[idx] = { x: q.x + dx, y: q.y + dy }; return; }
    if (idx === 0 && a.a) a.a = { x: a.a.x + dx, y: a.a.y + dy };
    else if (idx === 1 && a.b) a.b = { x: a.b.x + dx, y: a.b.y + dy };
  }
  let edit: { id: string; kind: "body" | "pt"; idx: number; last: Pt } | null = null;
  function startEdit(a: Annotation, kind: "body" | "pt", idx: number, e: PointerEvent) {
    e.stopPropagation(); e.preventDefault();
    setSelectedId(a.id);
    edit = { id: a.id, kind, idx, last: clientToImage(e.clientX, e.clientY) };
    const move = (ev: PointerEvent) => onEditMove(ev);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); onEditUp(); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }
  function onEditMove(ev: PointerEvent) {
    if (!edit) return;
    const uid = curUid(); if (!uid) return;
    const cur = clientToImage(ev.clientX, ev.clientY);
    const dx = cur.x - edit.last.x, dy = cur.y - edit.last.y;
    edit.last = cur;
    // Mutate the store WITHOUT persisting on every move — a single
    // save fires on pointer-up. Keeps the drag smooth + the DB quiet.
    setDocs(produce((m) => {
      const arr = m[uid]?.annotations as any[] | undefined; if (!arr) return;
      const a = arr.find((x) => x.id === edit!.id); if (!a) return;
      if (edit!.kind === "body") moveAnno(a, dx, dy);
      else movePoint(a, edit!.idx, dx, dy);
    }));
  }
  function onEditUp() {
    const uid = curUid(); edit = null;
    if (uid) void saveImageDoc(p.studyUid, uid, docs[uid]);
  }
  function undo() {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.annotations = d.annotations.slice(0, -1); });
  }
  function clearAnnos() {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.annotations = []; });
  }

  // ---------- image-label tier ----------
  function setProcedure(pr: Procedure) {
    setProcedureSig(pr);
    const uid = curUid(); if (uid) mutateDoc(uid, (d) => { d.procedure = pr; });
  }
  function patchLabel(patch: Partial<ImageLabel>) {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.image_label = { ...d.image_label, ...patch }; });
  }
  function clearLabel() {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.image_label = {}; });
  }

  // ---------- calibration ----------
  function setCalibration(c: Calibration | null) {
    const uid = curUid(); if (!uid) return;
    mutateDoc(uid, (d) => { d.calibration = c; });
  }
  /** mm label for a pixel length, honouring the current image's
   *  calibration; px when uncalibrated. */
  function lenLabel(px: number): string {
    const c = curDoc().calibration;
    if (c && c.mmPerPx > 0) return `${(px * c.mmPerPx).toFixed(1)} mm`;
    return `${px.toFixed(0)} px`;
  }

  // Metadata stamped onto each new annotation from the active lesion
  // class so it is colour-coded + carries clinical meaning.
  function annoMeta(): { color: string; lesion_id: string; lesion_name: string; grade_id: string } {
    const tx = taxonById(annoLesionId());
    if (tx) return { color: tx.color, lesion_id: tx.id, lesion_name: tx.name, grade_id: annoGradeId() || "" };
    const custom = annoLesionCustom().trim();
    return { color: STROKE, lesion_id: "", lesion_name: custom, grade_id: "" };
  }
  /** The grading scheme of the active annotation class, if any. */
  const annoGrades = createMemo(() => taxonById(annoLesionId())?.grades ?? []);

  // Clear the selection whenever the focused image changes — a handle
  // from the previous frame must never linger over a new one.
  createEffect(() => { void p.activeInstanceUid; setSelectedId(null); });

  // ---------- hydrate from server (once per instance) ----------
  const hydrated = new Set<string>();
  createEffect(() => {
    const uid = p.activeInstanceUid;
    if (!uid || hydrated.has(uid)) return;
    hydrated.add(uid);
    loadImageDoc(p.studyUid, uid).then((d) => {
      if (docs[uid] === undefined) {
        setDocs(uid, d);
        // Seed the session procedure from the first labelled image.
        if (d.procedure) setProcedureSig(d.procedure);
      }
    });
  });

  // ---------- coord helpers ----------
  function clientToImage(clientX: number, clientY: number): Pt {
    const img = imgEl();
    if (!img) return { x: 0, y: 0 };
    const rect = img.getBoundingClientRect();
    const z = zoom();
    return { x: (clientX - rect.left) / z, y: (clientY - rect.top) / z };
  }

  // ---------- pointer handling ----------
  let dragging = false;
  let panStart: Pt | null = null;
  let panOrigin: Pt | null = null;

  // Active-drag window listeners. Solid delegates pointer events at the
  // document and pointer capture under that delegation routed move/up
  // away from this handler. Driving the drag from window listeners
  // (added on down, removed on up) delivers move/up reliably without
  // setPointerCapture.
  let winMove: ((e: PointerEvent) => void) | null = null;
  let winUp:   ((e: PointerEvent) => void) | null = null;
  function endDrag() {
    if (winMove) window.removeEventListener("pointermove", winMove);
    if (winUp)   window.removeEventListener("pointerup", winUp);
    winMove = winUp = null;
  }
  function beginDrag() {
    endDrag();
    winMove = (ev: PointerEvent) => onPointerMove(ev);
    winUp   = (ev: PointerEvent) => { onPointerUp(ev); endDrag(); };
    window.addEventListener("pointermove", winMove);
    window.addEventListener("pointerup", winUp);
  }
  onCleanup(endDrag);

  function commitPolygon() {
    const pts = draftPts();
    if (pts && pts.length >= 3) {
      pushAnno({ id: crypto.randomUUID(), tool: "polygon", ...annoMeta(), pts });
    }
    setDraftPts(null); setPolyHover(null);
  }

  function onPointerDown(e: PointerEvent) {
    const tgt = e.target as HTMLElement;
    if (tgt.closest("[data-annotool=skip]")) return;
    const t = tool();
    const uid = p.activeInstanceUid;
    if (!uid) return;

    if (t === "select") {
      // A mark's own handler selects it (and stops propagation); a
      // pointerdown that reaches the viewport is on empty space → clear
      // the selection.
      setSelectedId(null);
      return;
    }

    if (t === "zoom") {
      e.preventDefault();
      zoomAround(e.clientX, e.clientY, e.shiftKey ? 1 / 1.4 : 1.4);
      return;
    }
    if (t === "text") {
      const ip = clientToImage(e.clientX, e.clientY);
      const txt = window.prompt("Caption", "");
      if (txt && txt.trim()) {
        pushAnno({ id: crypto.randomUUID(), tool: "text", ...annoMeta(), at: ip, text: txt.trim() });
      }
      return;
    }
    if (t === "polygon") {
      // Click-to-add-vertex. Clicking within 10px of the first vertex
      // (when we have ≥3) closes the polygon.
      e.preventDefault();
      const ip = clientToImage(e.clientX, e.clientY);
      const pts = draftPts();
      if (pts && pts.length >= 3) {
        const first = pts[0];
        if (Math.hypot(ip.x - first.x, ip.y - first.y) <= 10 / zoom()) { commitPolygon(); return; }
      }
      setDraftPts(pts ? [...pts, ip] : [ip]);
      setPolyHover(ip);
      return;
    }

    // Drag gestures.
    e.preventDefault();
    if (t === "pan") {
      dragging = true;
      panStart = { x: e.clientX, y: e.clientY };
      panOrigin = pan();
      setFitMode(false);
    } else if (t === "freehand") {
      setDraftPts([clientToImage(e.clientX, e.clientY)]);
    } else if (TWO_PT.has(t)) {
      const ip = clientToImage(e.clientX, e.clientY);
      setDraftStart(ip);
      setDraftPts([ip, ip]);
    }
    beginDrag();
  }

  function onPointerMove(e: PointerEvent) {
    const t = tool();
    if (t === "pan" && dragging && panStart && panOrigin) {
      setPan({ x: panOrigin.x + (e.clientX - panStart.x), y: panOrigin.y + (e.clientY - panStart.y) });
      return;
    }
    if (t === "freehand" && draftPts()) {
      const ip = clientToImage(e.clientX, e.clientY);
      setDraftPts((arr) => arr ? [...arr, ip] : [ip]);
      return;
    }
    if (TWO_PT.has(t) && draftStart()) {
      setDraftPts([draftStart()!, clientToImage(e.clientX, e.clientY)]);
      return;
    }
  }

  // Hover for the polygon rubber-band (no drag in polygon mode).
  function onViewportPointerMove(e: PointerEvent) {
    if (tool() === "polygon" && draftPts()) setPolyHover(clientToImage(e.clientX, e.clientY));
  }

  function onPointerUp(_e: PointerEvent) {
    const t = tool();
    const uid = p.activeInstanceUid;
    const reset = () => { setDraftPts(null); setDraftStart(null); };
    if (!uid) { reset(); return; }
    if (t === "pan") { dragging = false; panStart = null; panOrigin = null; return; }

    if (t === "freehand" && draftPts() && draftPts()!.length >= 2) {
      const pts = draftPts()!;
      pushAnno({ id: crypto.randomUUID(), tool: "freehand", ...annoMeta(), pts });
    }
    if (TWO_PT.has(t) && draftPts() && draftPts()!.length === 2) {
      const [a, b] = draftPts()!;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len >= 4) {  // reject zero-length single-click no-ops
        if (t === "arrow")         pushAnno({ id: crypto.randomUUID(), tool: "arrow",    ...annoMeta(), a, b });
        else if (t === "distance") pushAnno({ id: crypto.randomUUID(), tool: "distance", ...annoMeta(), a, b });
        else if (t === "bbox")     pushAnno({ id: crypto.randomUUID(), tool: "bbox",     ...annoMeta(), a, b });
        else if (t === "calibrate") {
          const raw = window.prompt(
            "Calibrate: enter the REAL length of the line you drew, in millimetres.\n" +
            "Tip: draw over a known reference — open biopsy forceps span ≈ 7 mm.",
            "7");
          const mm = raw ? parseFloat(raw) : NaN;
          if (isFinite(mm) && mm > 0) setCalibration({ mmPerPx: mm / len, ref_mm: mm });
        }
      }
    }
    reset();
  }

  function zoomAround(clientX: number, clientY: number, factor: number) {
    const img = imgEl();
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const dx = clientX - rect.left, dy = clientY - rect.top;
    const oldZ = zoom();
    const newZ = Math.max(0.05, Math.min(20, oldZ * factor));
    const ratio = newZ / oldZ;
    setPan((cur) => ({ x: cur.x - dx * (ratio - 1), y: cur.y - dy * (ratio - 1) }));
    setZoom(newZ);
    setFitMode(false);
  }

  function fitToWindow() {
    const img = imgEl();
    if (!img || !img.naturalWidth || !viewportEl) return;
    const vw = viewportEl.clientWidth, vh = viewportEl.clientHeight;
    if (!vw || !vh) return;
    setZoom(Math.min(vw / img.naturalWidth, vh / img.naturalHeight));
    setPan({ x: 0, y: 0 });
    setFitMode(true);
  }
  function oneToOne() { setZoom(1 / dpr()); setPan({ x: 0, y: 0 }); setFitMode(false); }
  function resetView() { fitToWindow(); }

  // ---------- fullscreen ----------
  const [isFs, setIsFs] = createSignal(false);
  function toggleFullscreen() {
    const el = rootEl;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  }
  onMount(() => {
    const onFsChange = () => {
      setIsFs(!!document.fullscreenElement);
      requestAnimationFrame(() => { if (fitMode()) fitToWindow(); });
    };
    document.addEventListener("fullscreenchange", onFsChange);
    onCleanup(() => document.removeEventListener("fullscreenchange", onFsChange));
  });

  // Re-fit on panel resize, only in fit mode.
  onMount(() => {
    if (typeof ResizeObserver === "undefined" || !viewportEl) return;
    const ro = new ResizeObserver(() => { if (fitMode()) fitToWindow(); });
    ro.observe(viewportEl);
    onCleanup(() => ro.disconnect());
  });

  onMount(() => {
    function onKey(e: KeyboardEvent) {
      const tgt = e.target as HTMLElement | null;
      if (tgt && /^(INPUT|TEXTAREA|SELECT)$/.test(tgt.tagName)) return;
      if (e.key === "Escape") {
        if (draftPts()) { setDraftPts(null); setPolyHover(null); return; }
        if (selectedId()) { setSelectedId(null); return; }
      }
      if (e.key === "Enter" && tool() === "polygon") { commitPolygon(); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId()) {
        e.preventDefault();
        const id = selectedId()!; setSelectedId(null); removeAnno(id);
        return;
      }
      if (e.key === "s" || e.key === "v") setTool("select");
      else if (e.key === "z") setTool("zoom");
      else if (e.key === "h") setTool("pan");
      else if (e.key === "f") setTool("freehand");
      else if (e.key === "a") setTool("arrow");
      else if (e.key === "d") setTool("distance");
      else if (e.key === "g") setTool("polygon");
      else if (e.key === "b") setTool("bbox");
      else if (e.key === "t") setTool("text");
      else if (e.key === "c") setTool("calibrate");
      else if (e.key === "r") resetView();
      else if ((e.metaKey || e.ctrlKey) && e.key === "z") { e.preventDefault(); undo(); }
    }
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  // ---------- image URLs ----------
  function imgUrl(inst: StudyInstance): string {
    const tok = auth()?.accessToken ?? "";
    const qs = tok ? `?access_token=${encodeURIComponent(tok)}` : "";
    return `/viewer-api/image/studies/${inst.studyUid}/series/${inst.seriesUid}/instances/${inst.instanceUid}.jpg${qs}`;
  }

  const activeInst = createMemo<StudyInstance | undefined>(() =>
    p.instances.find((x) => x.instanceUid === p.activeInstanceUid));

  // ---------- annotation renderer (closure: reads calibration + zoom) ----------
  // Sizes come from sz() so strokes/labels stay constant in SCREEN px
  // (readable + never a hairline). In Select mode each mark gets a fat
  // transparent hit line/area (click to select + drag the body) and,
  // when selected, a white selection outline + draggable point handles.
  const SEL = "#ffffff";
  // Everything here is read through ACCESSORS (or inline signal/store
  // reads) — never snapshotted into a const — because renderAnno runs
  // once per row inside <For> and is NOT re-invoked when a signal
  // changes. Snapshotting froze the selection highlight (only refreshed
  // after an unrelated edit re-created the row) and froze bbox/polygon
  // geometry during a handle drag. Accessors keep them live.
  function renderAnno(a: Annotation) {
    const c = () => a.color || STROKE;
    const s = sz;                                  // memo accessor → call s()
    const interactive = () => tool() === "select";
    const selected = () => selectedId() === a.id;
    const dash = () => `${s().font * 0.4} ${s().font * 0.3}`;

    // High-contrast value/label with a dark casing under the glyphs
    // (paint-order stroke) so it reads on any mucosa colour. Args are
    // accessors so the label tracks geometry/zoom changes.
    const Label = (lx: () => number, ly: () => number, txt: () => string, anchor: "middle" | "start" = "middle") => (
      <text x={lx()} y={ly()} fill={c()} font-size={String(s().font)} text-anchor={anchor}
            font-family="ui-monospace, monospace"
            stroke={STROKE_HALO} stroke-width={s().font * 0.28}
            paint-order="stroke" stroke-linejoin="round">
        {txt()}
      </text>
    );
    // Draggable control-point handles (shown when selected in Select mode).
    const Handles = () => (
      <Show when={interactive() && selected()}>
        <For each={handlesOf(a)}>{(h) => (
          <circle cx={h.x} cy={h.y} r={s().handle} fill={SEL} stroke={c()} stroke-width={s().sel}
                  style={{ "pointer-events": "all", cursor: "pointer" }}
                  onPointerDown={(e) => startEdit(a, "pt", h.idx, e)} />
        )}</For>
      </Show>
    );

    if (a.tool === "freehand") {
      const pts = () => a.pts.map((q) => `${q.x},${q.y}`).join(" ");
      return (
        <g>
          <polyline points={pts()} stroke={STROKE_HALO} stroke-width={s().halo} fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.6" />
          <polyline points={pts()} stroke={c()} stroke-width={s().main} fill="none" stroke-linecap="round" stroke-linejoin="round" />
          <Show when={selected()}><polyline points={pts()} stroke={SEL} stroke-width={s().sel} fill="none" stroke-dasharray={dash()} /></Show>
          <Show when={interactive()}>
            <polyline points={pts()} stroke="transparent" stroke-width={s().hit} fill="none" stroke-linecap="round"
                      style={{ "pointer-events": "stroke", cursor: "move" }} onPointerDown={(e) => startEdit(a, "body", -1, e)} />
          </Show>
        </g>
      );
    }
    if (a.tool === "arrow") {
      const head = () => {
        const dx = a.b.x - a.a.x, dy = a.b.y - a.a.y, len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len, hl = s().font * 1.1;
        const ax = a.b.x - ux * hl + (-uy) * hl * 0.5, ay = a.b.y - uy * hl + ux * hl * 0.5;
        const bx = a.b.x - ux * hl - (-uy) * hl * 0.5, by = a.b.y - uy * hl - ux * hl * 0.5;
        return `${ax},${ay} ${a.b.x},${a.b.y} ${bx},${by}`;
      };
      return (
        <g>
          <line x1={a.a.x} y1={a.a.y} x2={a.b.x} y2={a.b.y} stroke={STROKE_HALO} stroke-width={s().halo} stroke-linecap="round" opacity="0.6" />
          <line x1={a.a.x} y1={a.a.y} x2={a.b.x} y2={a.b.y} stroke={c()} stroke-width={s().main} stroke-linecap="round" />
          <polyline points={head()} stroke={c()} stroke-width={s().main} fill="none" stroke-linecap="round" stroke-linejoin="round" />
          <Show when={interactive()}>
            <line x1={a.a.x} y1={a.a.y} x2={a.b.x} y2={a.b.y} stroke="transparent" stroke-width={s().hit}
                  style={{ "pointer-events": "stroke", cursor: "move" }} onPointerDown={(e) => startEdit(a, "body", -1, e)} />
          </Show>
          <Handles />
        </g>
      );
    }
    if (a.tool === "distance") {
      return (
        <g>
          <line x1={a.a.x} y1={a.a.y} x2={a.b.x} y2={a.b.y} stroke={STROKE_HALO} stroke-width={s().halo} stroke-linecap="round" opacity="0.6" />
          <line x1={a.a.x} y1={a.a.y} x2={a.b.x} y2={a.b.y} stroke={c()} stroke-width={s().main} stroke-linecap="round" />
          <circle cx={a.a.x} cy={a.a.y} r={s().main * 1.4} fill={c()} />
          <circle cx={a.b.x} cy={a.b.y} r={s().main * 1.4} fill={c()} />
          {Label(() => (a.a.x + a.b.x) / 2, () => (a.a.y + a.b.y) / 2 - s().font * 0.6,
                 () => lenLabel(Math.hypot(a.b.x - a.a.x, a.b.y - a.a.y)))}
          <Show when={interactive()}>
            <line x1={a.a.x} y1={a.a.y} x2={a.b.x} y2={a.b.y} stroke="transparent" stroke-width={s().hit}
                  style={{ "pointer-events": "stroke", cursor: "move" }} onPointerDown={(e) => startEdit(a, "body", -1, e)} />
          </Show>
          <Handles />
        </g>
      );
    }
    if (a.tool === "polygon") {
      const pts = () => a.pts.map((q) => `${q.x},${q.y}`).join(" ");
      const cx = () => a.pts.reduce((acc, q) => acc + q.x, 0) / a.pts.length;
      const cy = () => a.pts.reduce((acc, q) => acc + q.y, 0) / a.pts.length;
      return (
        <g>
          <polygon points={pts()} stroke={STROKE_HALO} stroke-width={s().halo} fill="none" opacity="0.5" />
          <polygon points={pts()} stroke={c()} stroke-width={s().main} fill={c()} fill-opacity="0.18" stroke-linejoin="round" />
          <Show when={selected()}><polygon points={pts()} stroke={SEL} stroke-width={s().sel} fill="none" stroke-dasharray={dash()} /></Show>
          <Show when={a.lesion_name}>{Label(cx, cy, () => a.lesion_name!)}</Show>
          <Show when={interactive()}>
            <polygon points={pts()} fill="transparent" stroke="transparent" stroke-width={s().hit}
                     style={{ "pointer-events": "all", cursor: "move" }} onPointerDown={(e) => startEdit(a, "body", -1, e)} />
          </Show>
          <Handles />
        </g>
      );
    }
    if (a.tool === "bbox") {
      const x = () => Math.min(a.a.x, a.b.x), y = () => Math.min(a.a.y, a.b.y);
      const w = () => Math.abs(a.b.x - a.a.x), h = () => Math.abs(a.b.y - a.a.y);
      return (
        <g>
          <rect x={x()} y={y()} width={w()} height={h()} stroke={STROKE_HALO} stroke-width={s().halo} fill="none" opacity="0.5" />
          <rect x={x()} y={y()} width={w()} height={h()} stroke={c()} stroke-width={s().main} fill={c()} fill-opacity="0.12" />
          <Show when={selected()}><rect x={x()} y={y()} width={w()} height={h()} stroke={SEL} stroke-width={s().sel} fill="none" stroke-dasharray={dash()} /></Show>
          <Show when={a.lesion_name}>{Label(x, () => y() - s().font * 0.5, () => a.lesion_name!, "start")}</Show>
          <Show when={interactive()}>
            <rect x={x()} y={y()} width={w()} height={h()} fill="transparent" stroke="transparent" stroke-width={s().hit}
                  style={{ "pointer-events": "all", cursor: "move" }} onPointerDown={(e) => startEdit(a, "body", -1, e)} />
          </Show>
          <Handles />
        </g>
      );
    }
    // text
    const pad = () => s().txt * 0.3, th = () => s().txt * 1.4;
    const tw = () => Math.max(a.text.length * s().txt * 0.62 + pad() * 2, s().txt * 2);
    return (
      <g>
        <rect x={a.at.x - pad()} y={a.at.y - th()} rx={s().txt * 0.2} ry={s().txt * 0.2} width={tw()} height={th()}
              fill="rgba(15, 23, 42, 0.82)" stroke={selected() ? SEL : c()} stroke-width={selected() ? s().sel : s().sel * 0.6}
              style={{ "pointer-events": interactive() ? "all" : "none", cursor: "move" }}
              onPointerDown={(e) => { if (tool() === "select") startEdit(a, "body", -1, e); }}
              onDblClick={() => { if (tool() === "select") { const t = window.prompt("Edit caption", a.text); if (t != null) setAnnoText(a.id, t.trim()); } }} />
        <text x={a.at.x} y={a.at.y - th() * 0.28} fill="#ffffff" font-size={String(s().txt)}
              style={{ "pointer-events": "none" }}
              font-family="system-ui, -apple-system, Segoe UI, sans-serif">{a.text}</text>
      </g>
    );
  }

  // ---------- derived UI lists ----------
  const landmarks = createMemo<Taxon[]>(() => taxaFor(procedure(), "landmark"));
  const lesions   = createMemo<Taxon[]>(() => taxaFor(procedure(), "lesion"));

  const labelSummary = createMemo(() => {
    const l = curDoc().image_label;
    const lm = l.landmark_name || (l.landmark_id ? taxonById(l.landmark_id)?.name : "") || "—";
    let les: string;
    if (l.lesion_name || l.lesion_id) {
      les = l.lesion_name || taxonById(l.lesion_id!)?.name || "—";
      const g = l.grade_id && taxonById(l.lesion_id || "")?.grades?.find((x) => x.id === l.grade_id);
      if (g) les += ` · ${g.name}`;
    } else if (l.normal === false) {
      les = "—";          // explicitly not assessed
    } else {
      les = "Normal";     // default
    }
    return { lm, les };
  });

  // Current <select> value for the lesion tier. Most endoscopy frames
  // are normal mucosa, so the DEFAULT (nothing recorded yet) is
  // "Normal" — the reader only changes it when there is a finding.
  // Picking "— not assessed —" stores normal:false explicitly, which
  // is the one state that reads back as blank.
  function lesionSelVal(l: ImageLabel): string {
    if (l.lesion_id) return l.lesion_id;
    if (l.lesion_name) return "__custom";
    if (l.normal === false) return "";   // explicitly set to not-assessed
    return "normal";                     // default
  }
  function onLesionSel(v: string) {
    if (v === "") patchLabel({ normal: false, lesion_id: "", lesion_name: "", grade_id: "" });
    else if (v === "normal") patchLabel({ normal: true, lesion_id: "", lesion_name: "", grade_id: "" });
    else if (v === "__custom") patchLabel({ normal: false, lesion_id: "", lesion_name: "(custom)", grade_id: "" });
    else { const tx = taxonById(v); patchLabel({ normal: false, lesion_id: v, lesion_name: tx?.name ?? "", grade_id: "" }); }
  }
  const labelLesionGrades = createMemo<{ id: string; name: string }[]>(() => {
    const l = curDoc().image_label;
    return (l.lesion_id ? taxonById(l.lesion_id)?.grades : undefined) ?? [];
  });
  const labelLesionIsCustom = () => {
    const l = curDoc().image_label;
    return !l.normal && !l.lesion_id && !!l.lesion_name;
  };
  const landmarkIsCustom = () => {
    const l = curDoc().image_label;
    return !l.landmark_id && !!l.landmark_name;
  };

  const selectCls = "w-full rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[12px] text-ink-900";

  return (
    <div ref={rootEl} class="flex h-full min-h-0 w-full flex-col bg-ink-0">
      {/* ===== Toolbar ===== */}
      <div class="flex flex-wrap items-center gap-1.5 border-b border-ink-300 bg-ink-100 px-3 py-1.5 text-[11px]">
        <span class="text-[10px] uppercase tracking-wider text-ink-700">tool</span>
        <For each={[
          { id: "select",    label: "Select",   title: "Select (S) — click a mark to edit it; drag its handles to reshape, drag the body to move, Del to delete" },
          { id: "pan",       label: "Pan",      title: "Pan (H) — drag to move" },
          { id: "zoom",      label: "Zoom",     title: "Zoom (Z) — click in, shift-click out" },
          { id: "freehand",  label: "Freehand", title: "Freehand pen (F)" },
          { id: "arrow",     label: "Arrow",    title: "Arrow (A) — drag tail → head" },
          { id: "distance",  label: "Distance", title: "Distance (D) — drag to measure (mm once calibrated)" },
          { id: "polygon",   label: "Polygon",  title: "Polygon segmentation (G) — click vertices; click first point or Enter to close; Esc to cancel" },
          { id: "bbox",      label: "Box",      title: "Bounding box (B) — drag a rectangle" },
          { id: "text",      label: "Text",     title: "Text caption (T) — click to drop" },
          { id: "calibrate", label: "Calibrate",title: "Calibrate (C) — draw over a known-size reference to set the mm scale" },
        ] as { id: Tool; label: string; title: string }[]}>{(t) => (
          <button class="rounded border px-2 py-0.5 text-[10px] uppercase tracking-wider transition"
                  classList={{
                    "bg-brand text-white border-brand": tool() === t.id,
                    "border-ink-300 text-ink-700 hover:text-white": tool() !== t.id,
                  }}
                  title={t.title}
                  onClick={() => setTool(t.id)}>{t.label}</button>
        )}</For>
        <span class="ml-2 h-4 w-px bg-ink-300" />
        <button class="btn-ghost !text-[10px]" title="Fit the whole frame to the panel (R)" onClick={fitToWindow}>fit</button>
        <button class="btn-ghost !text-[10px]" title="Actual pixels — 1 image px = 1 screen px (sharpest)" onClick={oneToOne}>1:1</button>
        <button class="btn-ghost !text-[10px]" title={isFs() ? "Exit fullscreen" : "Fullscreen"} onClick={toggleFullscreen}>{isFs() ? "exit full" : "full"}</button>
        <button class="btn-ghost !text-[10px]" title="Undo last annotation (Ctrl/Cmd+Z)" onClick={undo}>undo</button>
        <button class="btn-ghost !text-[10px]" title="Clear all annotations on this image" onClick={clearAnnos}>clear</button>
        {/* Save draft — image labels/annotations auto-save, but this
            mirrors the right-dock action and is reachable in fullscreen
            (where the dock is hidden). */}
        <Show when={p.onSaveDraft}>
          <button class="rounded border border-brand bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand transition hover:bg-brand hover:text-white"
                  title="Save draft (image labels + report)"
                  disabled={p.saveState?.() === "saving"}
                  onClick={() => p.onSaveDraft?.()}>
            {p.saveState?.() === "saving" ? "saving…" : p.saveState?.() === "saved" ? "saved ✓" : p.saveState?.() === "error" ? "retry save" : "save draft"}
          </button>
        </Show>
        <span class="ml-auto font-mono text-[10px] text-ink-700">
          zoom <span class="text-ink-900">{Math.round(zoom() * dpr() * 100)}%</span>
          {" · "}
          <Show when={curDoc().calibration} fallback={<span class="text-warn">uncalibrated</span>}>
            <span class="text-ok">calibrated</span>
          </Show>
          {" · "}
          {p.instances.length} <span class="text-ink-700">stills</span>
        </span>
      </div>

      {/* ===== Annotation class picker — shown when an annotation tool
          is active. Lesion vocabulary for the current procedure +
          custom free text + grade. Replaces the old generic ClassBar. ===== */}
      <Show when={isAnnotationTool(tool())}>
        <div class="flex flex-wrap items-center gap-1.5 border-b border-ink-300 bg-ink-100/70 px-3 py-1.5">
          <span class="text-[10px] uppercase tracking-wider text-ink-700">class</span>
          <For each={lesions()}>{(lx) => (
            <button class="rounded border px-2 py-0.5 text-[10px] transition"
                    classList={{ "text-white": annoLesionId() === lx.id, "text-ink-700 hover:text-white": annoLesionId() !== lx.id }}
                    style={annoLesionId() === lx.id ? { background: lx.color, "border-color": lx.color } : { "border-color": lx.color, color: lx.color }}
                    onClick={() => { setAnnoLesionId(lx.id); setAnnoLesionCustom(""); setAnnoGradeId(""); }}>
              {lx.name}
            </button>
          )}</For>
          {/* Custom free-text class — always available, for every tool,
              alongside the preset classes above. */}
          <input type="text" placeholder="+ custom label…" data-annotool="skip" title="Type a custom label instead of a preset"
                 value={annoLesionId() === "" ? annoLesionCustom() : ""}
                 onInput={(e) => { setAnnoLesionId(""); setAnnoLesionCustom(e.currentTarget.value); setAnnoGradeId(""); }}
                 class="w-32 rounded border border-dashed border-ink-300 bg-ink-200 px-2 py-0.5 text-[11px] text-ink-900" />
          {/* Grade for the active preset class */}
          <Show when={annoGrades().length > 0}>
            <select data-annotool="skip" class="rounded border border-ink-300 bg-ink-200 px-1 py-0.5 text-[11px] text-ink-900"
                    value={annoGradeId()} onChange={(e) => setAnnoGradeId(e.currentTarget.value)}>
              <option value="">grade…</option>
              <For each={annoGrades()}>{(g) => <option value={g.id}>{g.name}</option>}</For>
            </select>
          </Show>
        </div>
      </Show>

      {/* ===== Main row: viewport + label sidebar ===== */}
      <div class="flex min-h-0 flex-1">
        {/* ----- Focused viewport ----- */}
        <div ref={viewportEl}
             class="relative min-w-0 flex-1 overflow-hidden bg-black"
             style={{
               "cursor": tool() === "pan" ? "grab"
                       : tool() === "zoom" ? "zoom-in"
                       : tool() === "text" ? "text"
                       : tool() === "select" ? "default"
                       : "crosshair",
             }}
             onPointerDown={onPointerDown}
             onPointerMove={onViewportPointerMove}
             onWheel={(e) => {
               if (e.ctrlKey || e.metaKey || e.deltaY) {
                 e.preventDefault();
                 zoomAround(e.clientX, e.clientY, e.deltaY < 0 ? 1.1 : 1 / 1.1);
               }
             }}>
          <Show when={activeInst()} fallback={
            <div class="grid h-full place-items-center text-ink-700 text-sm">select a snapshot below</div>
          }>
            <div class="absolute left-1/2 top-1/2 will-change-transform"
                 style={{ transform: `translate(${pan().x}px, ${pan().y}px) translate(-50%, -50%) scale(${zoom()})` }}>
              <img ref={(el) => { setImgEl(el); captureDims(el); }}
                   src={imgUrl(activeInst()!)}
                   class="block max-w-none select-none"
                   draggable={false}
                   onLoad={(e) => { captureDims(e.currentTarget as HTMLImageElement); fitToWindow(); }}
                   alt={`stills ${activeInst()!.instanceNumber}`} />
              <Show when={imgEl() && natW() > 0}>
                <svg class="pointer-events-none absolute inset-0" width={natW()} height={natH()} viewBox={`0 0 ${natW()} ${natH()}`}>
                  <For each={getAnnos(p.activeInstanceUid)}>{(a) => renderAnno(a)}</For>

                  {/* Live drafts — scaled to screen px like the committed marks */}
                  <Show when={draftPts() && tool() === "freehand"}>
                    <polyline points={draftPts()!.map((q) => `${q.x},${q.y}`).join(" ")}
                              stroke={annoMeta().color} stroke-width={sz().main} fill="none" stroke-linecap="round" stroke-linejoin="round" />
                  </Show>
                  <Show when={draftPts() && tool() === "polygon"}>
                    {(() => {
                      const pts = draftPts()!;
                      const line = pts.map((q) => `${q.x},${q.y}`).join(" ");
                      const hov = polyHover();
                      const col = annoMeta().color;
                      const s = sz();
                      return (
                        <g>
                          <polyline points={line} stroke={col} stroke-width={s.main} fill="none" stroke-linejoin="round" />
                          <Show when={hov && pts.length > 0}>
                            <line x1={pts[pts.length - 1].x} y1={pts[pts.length - 1].y} x2={hov!.x} y2={hov!.y}
                                  stroke={col} stroke-width={s.main * 0.8} stroke-dasharray={`${s.font * 0.4} ${s.font * 0.3}`} opacity="0.7" />
                          </Show>
                          <For each={pts}>{(q, i) => (
                            <circle cx={q.x} cy={q.y} r={i() === 0 ? s.handle : s.handle * 0.7} fill={col} />
                          )}</For>
                        </g>
                      );
                    })()}
                  </Show>
                  <Show when={draftPts() && TWO_PT.has(tool()) && draftPts()!.length === 2}>
                    {(() => {
                      const [a, b] = draftPts()!;
                      const t = tool();
                      const col = t === "calibrate" ? "#f59e0b" : annoMeta().color;
                      const s = sz();
                      if (t === "bbox") {
                        const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
                        return <rect x={x} y={y} width={Math.abs(b.x - a.x)} height={Math.abs(b.y - a.y)} stroke={col} stroke-width={s.main} fill={col} fill-opacity="0.12" />;
                      }
                      const len = Math.hypot(b.x - a.x, b.y - a.y);
                      return (
                        <g>
                          <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={col} stroke-width={s.main} stroke-linecap="round"
                                stroke-dasharray={t === "calibrate" ? `${s.font * 0.5} ${s.font * 0.3}` : undefined} />
                          <Show when={t === "distance"}>
                            <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - s.font * 0.5} fill={col} font-size={String(s.font)}
                                  font-family="ui-monospace, monospace" text-anchor="middle"
                                  stroke={STROKE_HALO} stroke-width={s.font * 0.28} paint-order="stroke">{lenLabel(len)}</text>
                          </Show>
                          <Show when={t === "calibrate"}>
                            <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - s.font * 0.5} fill={col} font-size={String(s.font)}
                                  font-family="ui-monospace, monospace" text-anchor="middle"
                                  stroke={STROKE_HALO} stroke-width={s.font * 0.28} paint-order="stroke">{len.toFixed(0)} px → ? mm</text>
                          </Show>
                        </g>
                      );
                    })()}
                  </Show>
                </svg>
              </Show>
            </div>
          </Show>

          {/* HUD */}
          <div class="pointer-events-none absolute left-2 top-2 rounded bg-black/60 px-2 py-1 font-mono text-[10px] text-white/80">
            {/* Case ID is always shown so an anonymised study is still
                identifiable from the viewer alone. */}
            <span class="text-white">{caseId(p.studyUid)}</span>
            <Show when={activeInst()}>
              {" · "}STILL <span class="text-white">#{activeInst()!.instanceNumber}</span>
              <span class="ml-1 text-ink-700">/ {p.instances.length}</span>
            </Show>
          </div>
          <div class="pointer-events-none absolute right-2 top-2 rounded bg-black/60 px-2 py-1 font-mono text-[10px] text-white/80">
            tool <span class="text-brand">{tool()}</span>
            <Show when={tool() === "polygon" && draftPts()}>
              <span class="ml-1 text-white/60">· {draftPts()!.length} pts · click first/Enter to close</span>
            </Show>
            <Show when={tool() === "select"}>
              <span class="ml-1 text-white/60">· {selectedId() ? "drag handles / body · Del to delete" : "click a mark to edit"}</span>
            </Show>
          </div>
        </div>

        {/* ----- Label sidebar ----- */}
        <Show when={activeInst()}>
          <div data-annotool="skip" class="flex w-72 shrink-0 flex-col gap-3 overflow-y-auto border-l border-ink-300 bg-ink-100 p-3 text-[12px]">
            {/* Procedure */}
            <div>
              <div class="mb-1 text-[10px] uppercase tracking-wider text-ink-700">procedure</div>
              <div class="flex gap-1">
                <For each={[["egd", "Upper GI"], ["colonoscopy", "Colonoscopy"]] as [Procedure, string][]}>{([id, lbl]) => (
                  <button class="flex-1 rounded border px-2 py-1 text-[11px] transition"
                          classList={{ "bg-brand text-white border-brand": procedure() === id, "border-ink-300 text-ink-700 hover:text-white": procedure() !== id }}
                          onClick={() => setProcedure(id)}>{lbl}</button>
                )}</For>
              </div>
            </div>

            {/* Image label — landmark + lesion */}
            <div class="rounded border border-ink-300 bg-ink-200/40 p-2">
              <div class="mb-2 flex items-center justify-between">
                <span class="text-[10px] uppercase tracking-wider text-ink-700">image label</span>
                <button class="text-[10px] text-ink-700 hover:text-danger" title="Clear this image's label" onClick={clearLabel}>clear</button>
              </div>

              {/* Tier 1 — anatomical landmark */}
              <label class="mb-1 block text-[10px] uppercase tracking-wider text-ink-700">1 · anatomical landmark</label>
              <select class={selectCls}
                      value={landmarkIsCustom() ? "__custom" : (curDoc().image_label.landmark_id ?? "")}
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        if (v === "") patchLabel({ landmark_id: "", landmark_name: "" });
                        else if (v === "__custom") patchLabel({ landmark_id: "", landmark_name: "(custom)" });
                        else { const tx = taxonById(v); patchLabel({ landmark_id: v, landmark_name: tx?.name ?? "" }); }
                      }}>
                <option value="">— none —</option>
                <For each={landmarks()}>{(lm) => <option value={lm.id}>{lm.name}</option>}</For>
                <option value="__custom">Custom…</option>
              </select>
              <Show when={landmarkIsCustom()}>
                <input type="text" placeholder="custom landmark" class={`${selectCls} mt-1`}
                       value={curDoc().image_label.landmark_name ?? ""}
                       onInput={(e) => patchLabel({ landmark_name: e.currentTarget.value })} />
              </Show>

              {/* Tier 2 — lesion (or Normal) */}
              <label class="mb-1 mt-3 block text-[10px] uppercase tracking-wider text-ink-700">2 · lesion (if any)</label>
              <select class={selectCls} value={lesionSelVal(curDoc().image_label)} onChange={(e) => onLesionSel(e.currentTarget.value)}>
                <option value="">— not assessed —</option>
                <option value="normal">Normal — no lesion</option>
                <For each={lesions()}>{(lx) => <option value={lx.id}>{lx.name}</option>}</For>
                <option value="__custom">Custom…</option>
              </select>
              <Show when={labelLesionIsCustom()}>
                <input type="text" placeholder="custom lesion" class={`${selectCls} mt-1`}
                       value={curDoc().image_label.lesion_name ?? ""}
                       onInput={(e) => patchLabel({ lesion_name: e.currentTarget.value })} />
              </Show>
              <Show when={labelLesionGrades().length > 0}>
                <select class={`${selectCls} mt-1`} value={curDoc().image_label.grade_id ?? ""} onChange={(e) => patchLabel({ grade_id: e.currentTarget.value })}>
                  <option value="">grade…</option>
                  <For each={labelLesionGrades()}>{(g) => <option value={g.id}>{g.name}</option>}</For>
                </select>
              </Show>

              <div class="mt-2 border-t border-ink-300 pt-2 text-[11px] text-ink-700">
                <div>landmark: <span class="text-ink-900">{labelSummary().lm}</span></div>
                <div>lesion: <span class="text-ink-900">{labelSummary().les}</span></div>
              </div>
            </div>

            {/* Calibration */}
            <div class="rounded border border-ink-300 bg-ink-200/40 p-2">
              <div class="mb-1 flex items-center justify-between">
                <span class="text-[10px] uppercase tracking-wider text-ink-700">scale</span>
                <Show when={curDoc().calibration}>
                  <button class="text-[10px] text-ink-700 hover:text-danger" onClick={() => setCalibration(null)}>reset</button>
                </Show>
              </div>
              <Show when={curDoc().calibration}
                    fallback={<p class="text-[11px] leading-snug text-ink-700">Endoscopy frames carry no DICOM pixel size. Use <span class="text-ink-900">Calibrate</span> over a known reference (e.g. open forceps ≈ 7 mm) to measure in mm.</p>}>
                <p class="text-[11px] text-ink-900">
                  1 px = {curDoc().calibration!.mmPerPx.toFixed(4)} mm
                  <span class="text-ink-700"> (ref {curDoc().calibration!.ref_mm} mm)</span>
                </p>
              </Show>
            </div>

            {/* Annotation list — click a row (or the mark itself) to
                select + edit: re-class, grade, caption, reshape on image,
                delete. */}
            <div class="rounded border border-ink-300 bg-ink-200/40 p-2">
              <div class="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-700">
                <span>annotations · {getAnnos(curUid()).length}</span>
                <Show when={selectedId()}><span class="text-brand">editing</span></Show>
              </div>
              <Show when={getAnnos(curUid()).length > 0} fallback={<p class="text-[11px] text-ink-700">No marks yet — pick a tool and draw. Then use <span class="text-ink-900">Select</span> to edit.</p>}>
                <ul class="flex flex-col gap-1">
                  <For each={getAnnos(curUid())}>{(a) => {
                    const isSel = () => selectedId() === a.id;
                    const aGrades = () => taxonById(a.lesion_id)?.grades ?? [];
                    return (
                      <li class="rounded border bg-ink-0/40 px-1.5 py-1"
                          classList={{ "border-brand ring-1 ring-brand/50": isSel(), "border-transparent": !isSel() }}>
                        <div class="flex items-center gap-1" style={{ cursor: "pointer" }}
                             onClick={() => { setTool("select"); setSelectedId(a.id); }}>
                          <span class="h-3 w-3 shrink-0 rounded-sm" style={{ background: a.color }} />
                          <span class="w-12 shrink-0 text-[10px] uppercase text-ink-700">{a.tool}</span>
                          <select class="min-w-0 flex-1 rounded border border-ink-300 bg-ink-200 px-1 py-0.5 text-[10px] text-ink-900"
                                  value={a.lesion_id ?? ""}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => { setSelectedId(a.id); reclassAnno(a.id, e.currentTarget.value); }}>
                            <option value="">{a.lesion_name && !a.lesion_id ? a.lesion_name : "—"}</option>
                            <For each={lesions()}>{(lx) => <option value={lx.id}>{lx.name}</option>}</For>
                          </select>
                          <button class="shrink-0 px-1 text-[12px] text-ink-700 hover:text-danger" title="Delete (Del)"
                                  onClick={(e) => { e.stopPropagation(); if (isSel()) setSelectedId(null); removeAnno(a.id); }}>✕</button>
                        </div>
                        <Show when={isSel()}>
                          <div class="mt-1 flex flex-col gap-1 border-t border-ink-300 pt-1">
                            {/* Custom free-text label — available on EVERY
                                tool (bbox / freehand / polygon / …), in
                                addition to the preset classes above. */}
                            <input type="text" placeholder="+ custom label…"
                                   value={!a.lesion_id ? ((a as any).lesion_name ?? "") : ""}
                                   onInput={(e) => setAnnoCustomLesion(a.id, e.currentTarget.value)}
                                   class="w-full rounded border border-ink-300 bg-ink-200 px-1 py-0.5 text-[11px] text-ink-900" />
                            <Show when={aGrades().length > 0}>
                              <select class="w-full rounded border border-ink-300 bg-ink-200 px-1 py-0.5 text-[10px] text-ink-900"
                                      value={a.grade_id ?? ""} onChange={(e) => setAnnoGrade(a.id, e.currentTarget.value)}>
                                <option value="">grade…</option>
                                <For each={aGrades()}>{(g) => <option value={g.id}>{g.name}</option>}</For>
                              </select>
                            </Show>
                            <Show when={a.tool === "text"}>
                              <input type="text" class="w-full rounded border border-ink-300 bg-ink-200 px-1 py-0.5 text-[11px] text-ink-900"
                                     value={(a as any).text ?? ""} onInput={(e) => setAnnoText(a.id, e.currentTarget.value)} />
                            </Show>
                            <p class="text-[10px] text-ink-700">Drag the handles on the image to reshape, or the body to move.</p>
                          </div>
                        </Show>
                      </li>
                    );
                  }}</For>
                </ul>
              </Show>
            </div>
          </div>
        </Show>
      </div>

      {/* ===== Contact sheet ===== */}
      <div class="shrink-0 border-t border-ink-300 bg-ink-100 p-2">
        <div class="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-700">
          <span>contact sheet · {p.instances.length}</span>
          <span class="font-mono">click thumb to open · labels stay per-image</span>
        </div>
        <div class="flex gap-2 overflow-x-auto pb-1"
             ref={(el) => {
               // Mouse wheel scrolls the rail horizontally so the reader
               // can sweep through a long contact sheet without reaching
               // for the scrollbar. Vertical wheel delta (the common
               // case) is mapped to scrollLeft; a trackpad's horizontal
               // delta is honoured too.
               el.addEventListener("wheel", (e) => {
                 const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
                 if (d === 0) return;
                 const max = el.scrollWidth - el.clientWidth;
                 if (max <= 0) return;                 // nothing hidden — let the page scroll
                 if ((d < 0 && el.scrollLeft <= 0) || (d > 0 && el.scrollLeft >= max)) return;
                 e.preventDefault();
                 el.scrollLeft += d;
               }, { passive: false });
             }}>
          <For each={p.instances}>{(inst) => {
            const isActive = () => inst.instanceUid === p.activeInstanceUid;
            const d = () => docs[inst.instanceUid];
            const annoCount = () => d()?.annotations?.length ?? 0;
            const labelled = () => { const l = d()?.image_label; return !!(l && (l.landmark_id || l.landmark_name || l.lesion_id || l.lesion_name || l.normal)); };
            return (
              <button data-annotool="skip" type="button"
                      onClick={() => p.onPickInstance?.(inst.instanceUid)}
                      class="group relative shrink-0 rounded border bg-black transition"
                      classList={{ "border-brand ring-2 ring-brand/40": isActive(), "border-ink-300 hover:border-brand": !isActive() }}
                      title={`#${inst.instanceNumber} · ${inst.instanceUid.slice(-12)}`}>
                <img src={imgUrl(inst)} class="h-20 w-28 object-contain" loading="lazy" alt={`thumb ${inst.instanceNumber}`} />
                <span class="absolute bottom-0 left-0 right-0 truncate bg-black/55 px-1 py-0.5 font-mono text-[9px] text-white">
                  #{inst.instanceNumber}
                  <Show when={labelled()}><span class="ml-1 text-ok">◆</span></Show>
                  <Show when={annoCount() > 0}><span class="ml-1 text-brand">● {annoCount()}</span></Show>
                </span>
              </button>
            );
          }}</For>
        </div>
      </div>
    </div>
  );
}
